import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL || '';

const cache = {};
// Bumped on every invalidation of an entity, so a response that was already in
// flight when the definitions changed never writes its stale list back into
// the cache.
const generation = {};
// Mounted hooks, told when a definition changes elsewhere (the import wizard,
// the admin Custom Fields tab) so an open page refetches instead of showing
// the old column list until reload (#972).
const subscribers = new Set();

/**
 * Forget the cached definitions of one entity ('contact' | 'account' | …), or
 * of every entity when called without one, and tell mounted hooks to refetch.
 */
export function invalidateCustomFields(entity) {
    const keys = entity ? [entity] : [...new Set([...Object.keys(cache), ...Object.keys(generation)])];
    keys.forEach((key) => {
        delete cache[key];
        generation[key] = (generation[key] || 0) + 1;
    });
    subscribers.forEach((notify) => notify(entity));
}

export default function useCustomFields(entity) {
    const { getAuthHeader } = useAuth();
    const [fields, setFields] = useState([]);
    const [loading, setLoading] = useState(true);
    const [version, setVersion] = useState(0);

    useEffect(() => {
        const notify = (changed) => {
            if (!changed || changed === entity) setVersion((v) => v + 1);
        };
        subscribers.add(notify);
        return () => { subscribers.delete(notify); };
    }, [entity]);

    useEffect(() => {
        if (!entity) { setLoading(false); return undefined; }

        // Use cache if available
        if (cache[entity]) {
            setFields(cache[entity]);
            setLoading(false);
            return undefined;
        }

        let live = true;
        const startedAt = generation[entity] || 0;
        const load = async () => {
            try {
                const res = await axios.get(`${API}/api/admin/custom-fields`, {
                    headers: getAuthHeader(),
                    params: { entity },
                });
                const data = res.data || [];
                if ((generation[entity] || 0) === startedAt) cache[entity] = data;
                if (live) setFields(data);
            } catch {
                if (live) setFields([]);
            }
            finally { if (live) setLoading(false); }
        };
        load();
        return () => { live = false; };
    }, [entity, getAuthHeader, version]);

    const invalidateCache = () => {
        invalidateCustomFields(entity);
    };

    return { fields, loading, invalidateCache };
}
